import './QualitativeObjectiveEditForm.css';

import React, { useEffect, useState } from 'react';
import B2BFormContainer from '../../../b2b-common/B2BFormContainer';
import DatesRangeEditor from '../dates/DatesRangeEditor';
import { generateKeyValueArray, generateSelectField } from '../../../b2b-common/helpers';
import { showNotification } from '../../../b2b-common/Notification';
import { useLoginContext } from '../../../../App';
import { useContentContext } from '../../../layout/Layout';
import { deleteObjective, saveNewObjective, updateObjective } from '../../../../services/b2b-plans-apiService';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const QualitativeObjectiveEditForm = ({
    objective,
    planUUID,
    areaId,
    areaIndicatorId,
    indicator = null,
    startDate,
    endDate,
    disabledDateRanges = [],
    onSave, onDelete, onCancel }) => {

    const { t } = useB2BTranslation('b2b-plans');
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();

    const [currentQualitativeType, setCurrentQualitativeType] = useState(null);
    const [currentObjective, setCurrentObjective] = useState(null);

    useEffect(() => {
        if (indicator) {
            // console.log('QualitativeObjectiveEditForm useEffect:', indicator);
            setCurrentQualitativeType(indicator.qualitativeTypeDTO);
            if (objective) {
                // console.log('QualitativeObjectiveEditForm useEffect objective:', objective);
                setCurrentObjective(objective);
            } else {
                setCurrentObjective({
                    objectiveType: "QUALITATIVE",
                    objectiveId: 0,
                    startDate: startDate,
                    endDate: endDate,
                    startObjectiveValue: null,
                    endObjectiveValue: null,
                });
            }
        }
    }, [indicator, objective, startDate, endDate]);

    const handleSave = async () => {
        if (objective === null) { // new objective
            const newObjective = { ...currentObjective, objectiveId: null };
            try {
                const savedObjective = await saveNewObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, newObjective);
                // showNotification(setNotification, 'Objective saved', 'success', 3000);
                onSave && onSave(savedObjective);
            } catch (error) {
                console.error('QualitativeObjectiveEditForm handleSave error:', error);
                showNotification(setNotification, t('Error saving new objective'), 'error', 5000);
            }
        } else { // update objective
            try {
                const updatedObjective = await updateObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, currentObjective);
                // showNotification(setNotification, 'Objective updated', 'success', 3000);
                onSave && onSave(updatedObjective);
            } catch (error) {
                console.error('QualitativeObjectiveEditForm handleSave error:', error);
                showNotification(setNotification, t('Error updating objective'), 'error', 5000);
            }
        }
    };

    const handleDeleteObjective = async () => {
        try {
            await deleteObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, currentObjective.objectiveId);
            onDelete && onDelete(currentObjective.objectiveId);
        } catch (error) {
            console.error('QualitativeObjectiveEditForm handleDeleteObjective error:', error);
            showNotification(setNotification, t('Error deleting objective: ') + error.message, 'error', 5000);
        }
    };

    const handleCancel = () => {
        onCancel && onCancel();
    };

    const handleChangeStartValue = (event) => {
        const weightedValue = currentQualitativeType.weightedValues.find(wv => wv.qualitativeWeightedValueId === event.target.value);
        setCurrentObjective({
            ...currentObjective,
            startObjectiveValue: {
                qualitativeTypeId: weightedValue.qualitativeTypeId,
                qualitativeWeightedValueId: weightedValue.qualitativeWeightedValueId,
                qualitativeValue: weightedValue.label,
                qualitativeWeight: weightedValue.weight,
            }
        });
    }

    const handleChangeEndValue = (event) => {
        const weightedValue = currentQualitativeType.weightedValues.find(wv => wv.qualitativeWeightedValueId === event.target.value);
        setCurrentObjective({
            ...currentObjective,
            endObjectiveValue: {
                qualitativeTypeId: weightedValue.qualitativeTypeId,
                qualitativeWeightedValueId: weightedValue.qualitativeWeightedValueId,
                qualitativeValue: weightedValue.label,
                qualitativeWeight: weightedValue.weight,
            }
        });
    }

    return (
        <B2BFormContainer
            title={objective ? t('Edit objective data') : t('New objective data')}
            buttonsConfig={[
                { label: t('Save'), onClick: handleSave },
                ...(objective !== null && objective.objectiveId !== 0 ? [{
                    label: t('Delete'),
                    confirm: true,
                    onConfirm: handleDeleteObjective,
                }] : []),
                { label: t('Cancel'), className: 'b2b-button-cancel white_03', onClick: handleCancel },
            ]}
            showDivider={false}
            className='qualitative-objective-edit-form b2b-form-01'>
            {startDate && endDate && currentObjective && (
                <div className='qualitative-objective-edit-form-dates'>
                    <DatesRangeEditor
                        datesRangeHolder={currentObjective}
                        setDatesRangeHolder={setCurrentObjective}
                        startDateLabel={t('Start date')}
                        endDateLabel={t('End date')}
                        startDateField='startDate'
                        endDateField='endDate'
                        className={'qualitative-objective-edit-form-obj-dates-range'}
                        disabledDateRanges={disabledDateRanges}
                    />
                </div>
            )}
            {currentQualitativeType && currentObjective && (
                <div className='qualitative-objective-edit-form-values'>
                    {generateSelectField(generateKeyValueArray(currentQualitativeType.weightedValues, 'qualitativeWeightedValueId', 'label'),
                        {
                            label: t('Start Value'),
                            value: currentObjective.startObjectiveValue ? currentObjective.startObjectiveValue.qualitativeWeightedValueId : '',
                            className: 'qualitative-objective-edit-form-start-value', onChange: handleChangeStartValue
                        })}
                    {generateSelectField(generateKeyValueArray(currentQualitativeType.weightedValues, 'qualitativeWeightedValueId', 'label'),
                        {
                            label: t('End Value'),
                            value: currentObjective.endObjectiveValue ? currentObjective.endObjectiveValue.qualitativeWeightedValueId : '',
                            className: 'qualitative-objective-edit-form-end-value', onChange: handleChangeEndValue
                        })}
                </div>
            )}
        </B2BFormContainer>
    );
};

export default QualitativeObjectiveEditForm;